import { BoltIcon } from '@/components/Icons'
import type { UiStrings } from '@/constants/ui-locale'
import { ASSIST_MODELS, parseAssistModel, type AssistModel } from './assistModel'
import CopilotPillSelect from './CopilotPillSelect'

type AssistModelSelectProps = {
  /** The model answering now — the server's word, not the last thing picked. */
  selected: AssistModel
  /** The model a choice is waiting on. The field is held while it is set. */
  pending: AssistModel | null
  /** Whether this server left the model out of `available`. */
  isUnavailable: (model: AssistModel) => boolean
  onSelect: (model: AssistModel) => void
  t: UiStrings
}

/**
 * Which assistant answers Copilot.
 *
 * Handed to the setup strip so it can stand on the same line as the project
 * picker: the two are the same kind of thing, what this panel is working
 * with, rather than anything about one answer. Passed through rather than
 * rendered there because the project picker can disappear on its own terms,
 * while this choice remains available in both ordinary meetings and shared
 * rooms.
 *
 * A model the server did not name says so in its own label and stays
 * choosable — such a question is answered with the model the server does
 * have rather than refused, so dropping the option would claim more than is
 * true. It is said in the label rather than by disabling the option, because
 * a disabled `<option>` is the one state a native `select` draws differently
 * on every platform.
 */
export default function AssistModelSelect({
  selected,
  pending,
  isUnavailable,
  onSelect,
  t,
}: AssistModelSelectProps) {
  const name: Record<AssistModel, string> = {
    gemini: t.assistModelGemini,
    gpt: t.assistModelGpt,
  }
  const hint: Record<AssistModel, string> = {
    gemini: t.assistModelGeminiHint,
    gpt: t.assistModelGptHint,
  }

  return (
    <CopilotPillSelect
      label={t.assistModelLabel}
      // The chip says which model and how it trades off, because that pair is
      // the whole choice; the field label it replaces said only "Model".
      display={`${name[selected]} — ${hint[selected]}`}
      testId="assist-model-select"
      icon={<BoltIcon className="h-3 w-3 shrink-0" />}
      value={selected}
      disabled={pending !== null}
      onChange={(next) => {
        const model = parseAssistModel(next)
        if (model) onSelect(model)
      }}
    >
      {ASSIST_MODELS.map((model) => (
        <option key={model} value={model}>
          {`${name[model]} — ${hint[model]}`}
          {/* Never on the model that is answering: a selection the
              server is honouring is not missing. */}
          {isUnavailable(model) && model !== selected ? ` (${t.assistModelUnavailable})` : ''}
        </option>
      ))}
    </CopilotPillSelect>
  )
}
